import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CRT_LABEL,
  CertificateUpload,
  Empty,
  EmitterForm,
  EmitterImport,
  ErrorState,
  MODEL_LABEL,
  PageHead,
  Pill,
  Skeleton,
  cnpj,
  date,
} from '@fa/ui';
import * as http from '../lib/api.js';
import { api } from '../lib/api.js';
import type { EmitterView } from '../lib/types.js';

/**
 * Empresas emissoras.
 *
 * O cartao de cada CNPJ mostra o que impede a emissao antes que ela pare:
 * validade do certificado, CSC configurado e numeracao de cada serie.
 */
export function Emitters(): ReactNode {
  const [cadastrando, setCadastrando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [iniciais, setIniciais] = useState<Record<string, string>>({});
  /** Emitente que está recebendo certificado; `null` esconde o formulário. */
  const [certificando, setCertificando] = useState<EmitterView | null>(null);

  const query = useQuery({
    queryKey: ['emitters'],
    queryFn: () => api<{ data: EmitterView[] }>('/v1/emitters'),
  });

  const emitters = query.data?.data ?? [];

  return (
    <>
      <PageHead
        title="Empresas emissoras"
        subtitle={`${emitters.length} CNPJ(s) cadastrado(s)`}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => setImportando(true)}>
              Importar de uma nota
            </button>{' '}
            <button className="btn btn-primary" onClick={() => setCadastrando(true)}>
              Cadastrar empresa
            </button>
          </>
        }
      />

      {importando && (
        <EmitterImport
          http={http}
          onImportado={(campos) => {
            setIniciais(campos);
            setImportando(false);
            setCadastrando(true);
          }}
          onFechar={() => setImportando(false)}
        />
      )}

      {cadastrando && (
        <EmitterForm
          http={http}
          iniciais={iniciais}
          onFechar={() => {
            setCadastrando(false);
            setIniciais({});
          }}
        />
      )}

      {certificando && (
        <CertificateUpload
          http={http}
          emitterId={certificando.id}
          nome={certificando.nome_fantasia ?? certificando.razao_social}
          onFechar={() => setCertificando(null)}
        />
      )}

      {query.isLoading && (
        <div className="card">
          <Skeleton rows={5} />
        </div>
      )}
      {query.isError && (
        <div className="card">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      )}

      {query.data && (
        <div className="emit">
          {emitters.map((emitter) => (
            <EmitterCard key={emitter.id} emitter={emitter} onCertificado={() => setCertificando(emitter)} />
          ))}
          <div
            className="card empty"
            style={{ display: 'grid', placeItems: 'center', borderStyle: 'dashed' }}
          >
            <div style={{ textAlign: 'center' }}>
              <Empty title="Adicionar outra empresa">
                Cadastre o CNPJ e envie o certificado A1 para começar a emitir.
              </Empty>
              <button className="btn btn-ghost" onClick={() => setCadastrando(true)}>
                Cadastrar empresa
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EmitterCard({
  emitter,
  onCertificado,
}: {
  emitter: EmitterView;
  onCertificado: () => void;
}): ReactNode {
  const cert = emitter.certificate;
  // A barra representa quanto ainda resta de um ciclo de 365 dias. Abaixo de
  // 30 dias ela fica ambar; abaixo de 5, vermelha — o mesmo escalonamento dos
  // avisos automaticos por e-mail e webhook.
  const restante = cert ? Math.max(0, Math.min(100, Math.round((cert.days_remaining / 365) * 100))) : 0;
  const tomCert = !cert ? 'bad' : cert.days_remaining <= 5 ? 'bad' : cert.days_remaining <= 30 ? 'warn' : '';

  return (
    <div className="card">
      <div className="hd">
        <div>
          <h3>{emitter.nome_fantasia ?? emitter.razao_social}</h3>
          <div className="cnpj">
            {emitter.razao_social} · {cnpj(emitter.cnpj)} · {emitter.municipio}, {emitter.uf}
          </div>
        </div>
        <Pill tone={emitter.status === 'active' ? 'ok' : emitter.status === 'suspended' ? 'bad' : 'warn'}>
          {emitter.status === 'active'
            ? 'Ativa'
            : emitter.status === 'suspended'
              ? 'Suspensa'
              : 'Falta certificado'}
        </Pill>
      </div>

      <div className="bd">
        <div className="cert">
          <span>Certificado A1</span>
          <div className="prog">
            <i className={tomCert} style={{ width: `${restante}%` }} />
          </div>
          <small>
            {cert
              ? `vence em ${cert.days_remaining} dia(s) · ${date(cert.expires_at)}`
              : 'nenhum certificado enviado'}
          </small>
          <button className="btn btn-sm btn-ghost" onClick={onCertificado}>
            {cert ? 'Substituir' : 'Enviar certificado'}
          </button>
        </div>

        <div className="series">
          {(['nfe', 'nfce', 'nfse'] as const).map((model) => {
            const serie = emitter.series.find((s) => s.model === model);
            return (
              <div key={model}>
                <span>
                  {MODEL_LABEL[model]}
                  {serie ? ` · série ${serie.serie}` : ''}
                </span>
                <b className="num">{serie ? `próx. ${serie.next_number.toLocaleString('pt-BR')}` : '—'}</b>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, color: 'var(--ink-2)', fontSize: '.88rem', flexWrap: 'wrap' }}>
          <span>{CRT_LABEL[emitter.crt] ?? 'Regime não informado'}</span>·
          <span>{emitter.has_csc ? 'CSC configurado' : 'CSC pendente (necessário para NFC-e)'}</span>·
          <span>{emitter.im ? 'NFS-e habilitada' : 'sem inscrição municipal'}</span>·
          <span>{emitter.environment === 'production' ? 'Produção' : 'Homologação'}</span>
        </div>
      </div>
    </div>
  );
}
